import { Session } from 'next-auth';
import { signOut } from 'next-auth/react';
import NProgress from 'nprogress';

import { API_CONFIG, APP_CONFIG } from '@/lib/config/api.config';
import { ApiError, logger, isErrorResponse } from '@/lib/utils/error.utils';

import { adminService } from '@/lib/services';

// --- NProgress Throttling Variables ---
let pendingRequests = 0;
const isBrowser = typeof window !== 'undefined';
let nprogressUpdateThrottled = false;
let nprogressThrottleTimeout: NodeJS.Timeout | null = null;
const NPROGRESS_THROTTLE_DELAY = 100; // ms
// --- End NProgress Throttling Variables ---

interface ApiRequestConfig {
  url: string;
  method: string;
  headers?: Record<string, string>;
  data?: any;
  isPublic?: boolean;
  signal?: AbortSignal;
}

// Track pending requests for progress bar

// Check if we're in a browser environment

// Throttled update function
function updateProgress() {
  if (!isBrowser || nprogressUpdateThrottled) return;

  const progress = Math.max(0.1, 1 - pendingRequests * 0.1);
  NProgress.set(progress);

  nprogressUpdateThrottled = true;
  if (nprogressThrottleTimeout) {
    clearTimeout(nprogressThrottleTimeout);
  }
  nprogressThrottleTimeout = setTimeout(() => {
    nprogressUpdateThrottled = false;
  }, NPROGRESS_THROTTLE_DELAY);
}

class ApiService {
  /**
   * Makes an HTTP request to the API
   * @param config The request configuration
   * @param session Optional session for authenticated requests
   * @returns The response data or null
   * @throws {ApiError} When the request fails
   */
  private async request<T>(
    config: ApiRequestConfig,
    session?: Session | null
  ): Promise<T | null> {
    const { url, method = 'GET', data, headers = {} } = config;
    const isFormData = data instanceof FormData;

    // Start progress tracking only in browser
    if (isBrowser && pendingRequests === 0) {
      NProgress.start(); // Start immediately
    }
    pendingRequests++;
    if (isBrowser) updateProgress(); // Call throttled update

    try {
      // Check if we have a valid session and token
      if (!session?.user?.accessToken && !config.isPublic) {
        await this.handleUnauthorized();
        throw new ApiError('No access token available', 401);
      }

      const requestHeaders: HeadersInit = {
        ...API_CONFIG.DEFAULT_HEADERS,
        ...(session?.user?.accessToken
          ? { Authorization: `Bearer ${session.user.accessToken}` }
          : {}),
        ...headers,
      };

      // Conditionally set Content-Type only if data is present and not FormData
      // FormData sets its own Content-Type with boundary
      if (data && !isFormData) {
        requestHeaders['Content-Type'] = 'application/json';
      } else if (!data) {
        // Remove Content-Type if no data is being sent
        delete requestHeaders['Content-Type'];
      }
      // For FormData, don't set Content-Type here; fetch handles it

      const requestConfig: RequestInit = {
        method,
        credentials: 'include',
        mode: 'cors',
        signal: config.signal,
        headers: requestHeaders,
      };

      if (data) {
        requestConfig.body = isFormData ? data : JSON.stringify(data);
      }

      const response = await fetch(
        `${API_CONFIG.BASE_URL}${url}`,
        requestConfig
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        logger.error('API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          data: errorData,
        });

        if (response.status === 401) {
          await this.handleUnauthorized();
          throw new ApiError('Session expired. Please log in again.', 401);
        }

        throw new ApiError(
          isErrorResponse(errorData) ? errorData.error : 'API request failed',
          response.status
        );
      }

      // Return null for 204 or empty responses without attempting to parse JSON
      if (
        response.status === 204 ||
        response.headers.get('Content-Length') === '0'
      ) {
        return null;
      }

      // Check if the response is empty before parsing
      const text = await response.text();
      if (!text || text.trim() === '') {
        return null;
      }

      try {
        return JSON.parse(text);
      } catch (parseError) {
        logger.error('JSON Parsing Error:', {
          error: parseError,
          responseText: text,
        });
        return null;
      }
    } catch (error) {
      logger.warn('API Request Error:', error);
      throw error;
    } finally {
      // Update progress tracking
      pendingRequests = Math.max(0, pendingRequests - 1);
      if (isBrowser) {
        updateProgress(); // Call throttled update
        if (pendingRequests === 0) {
          // Ensure NProgress finishes promptly even if last set was throttled
          if (nprogressThrottleTimeout) {
            clearTimeout(nprogressThrottleTimeout);
            nprogressThrottleTimeout = null;
          }
          nprogressUpdateThrottled = false;
          NProgress.done(); // Finish immediately
        }
      }
    }
  }

  /**
   * Handles unauthorized requests by clearing cache and redirecting to login
   */
  private async handleUnauthorized() {
    try {
      await adminService.clearCache();

      // Only call signOut in browser context
      if (typeof window !== 'undefined') {
        await signOut({ redirect: false });
        window.location.href = APP_CONFIG.ROUTES.LOGIN;
      }
    } catch (error) {
      logger.error('Error during unauthorized handling:', error);
      // Only redirect in browser context
      if (typeof window !== 'undefined') {
        window.location.href = APP_CONFIG.ROUTES.LOGIN;
      }
    }
  }

  /**
   * Makes a GET request to the API
   * @param url The request URL
   * @param session Optional session for authenticated requests
   * @param isPublic Whether the endpoint is public
   * @param signal Optional AbortSignal for request cancellation
   */
  async get<T>(
    url: string,
    session?: Session | null,
    isPublic?: boolean,
    signal?: AbortSignal
  ): Promise<T | null> {
    return this.request<T>({ url, method: 'GET', isPublic, signal }, session);
  }

  /**
   * Makes a POST request to the API
   * @param url The request URL
   * @param data The request body
   * @param session Optional session for authenticated requests
   * @param isPublic Whether the endpoint is public
   * @param signal Optional AbortSignal for request cancellation
   */
  async post<T>(
    url: string,
    data: any,
    session?: Session | null,
    isPublic?: boolean,
    signal?: AbortSignal
  ): Promise<T | null> {
    return this.request<T>(
      { url, method: 'POST', data, isPublic, signal },
      session
    );
  }

  /**
   * Makes a PUT request to the API
   * @param url The request URL
   * @param data The request body
   * @param session Optional session for authenticated requests
   * @param signal Optional AbortSignal for request cancellation
   */
  async put<T>(
    url: string,
    data: any,
    session?: Session | null,
    signal?: AbortSignal
  ): Promise<T | null> {
    return this.request<T>({ url, method: 'PUT', data, signal }, session);
  }

  /**
   * Makes a PATCH request to the API
   * @param url The request URL
   * @param data The request body
   * @param session Optional session for authenticated requests
   * @param signal Optional AbortSignal for request cancellation
   */
  async patch<T>(
    url: string,
    data: any,
    session?: Session | null,
    signal?: AbortSignal
  ): Promise<T | null> {
    return this.request<T>({ url, method: 'PATCH', data, signal }, session);
  }

  /**
   * Makes a DELETE request to the API
   * @param url The request URL
   * @param session Optional session for authenticated requests
   * @param signal Optional AbortSignal for request cancellation
   */
  async delete<T>(
    url: string,
    session?: Session | null,
    signal?: AbortSignal
  ): Promise<T | null> {
    return this.request<T>({ url, method: 'DELETE', signal }, session);
  }
}

export const apiService = new ApiService();
